import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { Constants } from "../constants/constants";
import { ResponseMessage } from "../models/ResponseMessage";

const instance = axios.create({
  withCredentials: true,
});

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshPromise: Promise<string> | null = null;
let isRedirectingToLogin = false;

// Public/auth endpoints that must never trigger an auto-refresh.
const REFRESH_EXCLUDED_PATHS = [
  "/accounts/login",
  "/accounts/register",
  "/accounts/refresh",
  "/accounts/enable-mfa",
  "/accounts/confirm-mfa",
  "/accounts/verify-mfa",
  "/accounts/request-password-reset",
  "/accounts/reset-password",
  "/accounts/verify-email",
];

const getAccessToken = (): string =>
  localStorage.getItem(Constants.ACCESS_TOKEN) || "";

const clearSession = () => {
  localStorage.removeItem(Constants.ACCESS_TOKEN);
  localStorage.removeItem(Constants.USER_INFO);
  localStorage.removeItem(Constants.ACTIVE_ORGANIZATION);
};

const redirectToLogin = () => {
  if (isRedirectingToLogin) return;
  if (!isAuthenticatedPath(window.location.pathname)) return;
  isRedirectingToLogin = true;
  clearSession();
  window.location.assign("/login");
};

const isExcludedPath = (url?: string): boolean =>
  REFRESH_EXCLUDED_PATHS.some((path) => url?.includes(path));

// Only authenticated app sections should be bounced to /login when the
// session expires. Public pages (login, register, invite, reset-password,
// marketing site, etc.) must never auto-redirect, otherwise visiting e.g.
// /invite with a stale token would bounce the visitor to /login.
const AUTH_PATH_PREFIXES = ["/dashboard", "/messaging", "/organizations"];
const isAuthenticatedPath = (path: string): boolean =>
  AUTH_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );

/**
 * Single-flight refresh: only one /refresh call runs at a time.
 * Concurrent 401s await the same promise and share the new token.
 */
const refreshAccessToken = async (): Promise<string> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const response = await axios.post(
      `${Constants.API_BASE_URL}/accounts/refresh`,
      null,
      { withCredentials: true },
    );

    const message = ResponseMessage.fromJson<{ accessToken?: string }>(
      response.data,
    );

    if (message.success && message.result?.accessToken) {
      localStorage.setItem(Constants.ACCESS_TOKEN, message.result.accessToken);
      return message.result.accessToken;
    }

    throw new Error(message.message || "Session refresh failed");
  })()
    .catch((error) => {
      redirectToLogin();
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const retryWithFreshToken = (
  original: RetriableConfig,
): Promise<AxiosResponse> => {
  original._retry = true;
  return refreshAccessToken().then((newToken) => {
    original.headers.Authorization = `Bearer ${newToken}`;
    return instance(original);
  });
};

// Attach the access token to every request unless it already has one.
instance.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token && config.headers && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

instance.interceptors.response.use(
  (response) => {
    // Backend quirk: auth failures come back as HTTP 200 with a
    // `{ success: false, responseCode: 401 }` envelope. Treat those
    // like a real 401 when the request carried a bearer token.
    const body = response.data as { success?: boolean; responseCode?: number };
    const original = response.config as RetriableConfig;

    const isEnvelope401 =
      body !== null &&
      typeof body === "object" &&
      body.success === false &&
      body.responseCode === 401;

    if (
      isEnvelope401 &&
      !original._retry &&
      !isExcludedPath(original.url) &&
      original.headers.Authorization
    ) {
      return retryWithFreshToken(original);
    }

    return response;
  },
  (error) => {
    const original = error.config as RetriableConfig | undefined;

    if (
      original &&
      error.response?.status === 401 &&
      !original._retry &&
      !isExcludedPath(original.url)
    ) {
      return retryWithFreshToken(original);
    }

    return Promise.reject(error);
  },
);

export default instance;